// Tarot Card Data - Complete Deck with Info

export const MAJOR_ARCANA_NAMES = [
    "The Fool", "The Magician", "The High Priestess", "The Empress", "The Emperor",
    "The Hierophant", "The Lovers", "The Chariot", "Strength", "The Hermit",
    "Wheel of Fortune", "Justice", "The Hanged Man", "Death", "Temperance",
    "The Devil", "The Tower", "The Star", "The Moon", "The Sun", "Judgement", "The World"
];

export const SUITS = ["Wands", "Cups", "Swords", "Pentacles"];
export const COURT_CARDS = ["Page", "Knight", "Queen", "King"];
export const MINOR_ARCANA_NUMBERS = ["Ace", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten"];

export const MINOR_ARCANA_NAMES = SUITS.flatMap(suit => [
    ...MINOR_ARCANA_NUMBERS.map(num => `${num} of ${suit}`),
    ...COURT_CARDS.map(court => `${court} of ${suit}`)
]);

export const TAROT_DECK_NAMES = [...MAJOR_ARCANA_NAMES, ...MINOR_ARCANA_NAMES];

// Card meanings and emojis
const CARD_INFO = {
    "The Fool": { description: "New beginnings, innocence, spontaneity, a free spirit.", emoji: "✨" },
    "The Magician": { description: "Manifestation, resourcefulness, power, inspired action.", emoji: "🪄" },
    "The High Priestess": { description: "Intuition, sacred knowledge, divine feminine, the subconscious mind.", emoji: "🌙" },
    "The Empress": { description: "Femininity, beauty, nature, nurturing, abundance.", emoji: "🌿" },
    "The Emperor": { description: "Authority, establishment, structure, a father figure.", emoji: "🏛️" },
    "The Hierophant": { description: "Spiritual wisdom, religious beliefs, conformity, tradition, institutions.", emoji: "📜" },
    "The Lovers": { description: "Love, harmony, relationships, values alignment, choices.", emoji: "💞" },
    "The Chariot": { description: "Control, willpower, victory, assertion, determination.", emoji: "🏆" },
    "Strength": { description: "Courage, persuasion, influence, compassion.", emoji: "🦁" },
    "The Hermit": { description: "Soul-searching, introspection, guidance, solitude.", emoji: "🏮" },
    "Wheel of Fortune": { description: "Good luck, karma, life cycles, destiny, a turning point.", emoji: "☸️" },
    "Justice": { description: "Fairness, truth, cause and effect, law.", emoji: "⚖️" },
    "The Hanged Man": { description: "Pause, surrender, letting go, new perspectives.", emoji: "🙃" },
    "Death": { description: "Endings, change, transformation, transition.", emoji: "🦋" },
    "Temperance": { description: "Balance, moderation, patience, purpose.", emoji: "⚗️" },
    "The Devil": { description: "Shadow self, attachment, addiction, restriction, sexuality.", emoji: "⛓️" },
    "The Tower": { description: "Sudden change, upheaval, chaos, revelation, awakening.", emoji: "⚡️" },
    "The Star": { description: "Hope, faith, purpose, renewal, spirituality.", emoji: "⭐" },
    "The Moon": { description: "Illusion, fear, anxiety, subconscious, intuition.", emoji: "🌕" },
    "The Sun": { description: "Positivity, fun, warmth, success, vitality.", emoji: "☀️" },
    "Judgement": { description: "Rebirth, inner calling, absolution.", emoji: "🎺" },
    "The World": { description: "Completion, integration, accomplishment, travel.", emoji: "🌍" },
    // Wands
    "Ace of Wands": { description: "New spark, inspiration, potential, growth.", emoji: "🌱🔥" },
    "Two of Wands": { description: "Planning, decisions, foresight, discovery.", emoji: "🗺️" },
    "Three of Wands": { description: "Expansion, foresight, overseas opportunities, progress.", emoji: "🚢" },
    "Four of Wands": { description: "Celebration, harmony, homecoming, stability.", emoji: "🎉" },
    "Five of Wands": { description: "Conflict, competition, disagreements, tension.", emoji: "💥" },
    "Six of Wands": { description: "Victory, success, public recognition, acclaim.", emoji: "🏅" },
    "Seven of Wands": { description: "Challenge, perseverance, defense, standing your ground.", emoji: "🛡️" },
    "Eight of Wands": { description: "Speed, action, progress, swift change, travel.", emoji: "✈️" },
    "Nine of Wands": { description: "Resilience, courage, persistence, last stand.", emoji: "🤕" },
    "Ten of Wands": { description: "Burden, responsibility, hard work, stress.", emoji: "🏋️" },
    "Page of Wands": { description: "Enthusiasm, exploration, inspiration, free spirit.", emoji: "💌🔥" },
    "Knight of Wands": { description: "Energy, passion, adventure, impulsiveness.", emoji: "🐎🔥" },
    "Queen of Wands": { description: "Courage, confidence, independence, determination.", emoji: "👑🔥" },
    "King of Wands": { description: "Leadership, vision, entrepreneurship, honor.", emoji: "🦁🔥" },
    // Cups
    "Ace of Cups": { description: "New love, emotional beginnings, intuition, creativity.", emoji: "💖💧" },
    "Two of Cups": { description: "Partnership, mutual attraction, connection, union.", emoji: "🥂" },
    "Three of Cups": { description: "Celebration, friendship, community, joy.", emoji: "💃💧" },
    "Four of Cups": { description: "Apathy, contemplation, missed opportunities, re-evaluation.", emoji: "🤔💧" },
    "Five of Cups": { description: "Loss, regret, disappointment, grief.", emoji: "😥💧" },
    "Six of Cups": { description: "Nostalgia, childhood memories, innocence, reunion.", emoji: "🎁💧" },
    "Seven of Cups": { description: "Choices, illusions, fantasy, wishful thinking.", emoji: "💭💧" },
    "Eight of Cups": { description: "Withdrawal, escapism, moving on, seeking truth.", emoji: "🚶‍♀️💧" },
    "Nine of Cups": { description: "Wishes fulfilled, contentment, satisfaction, gratitude.", emoji: "😊💧" },
    "Ten of Cups": { description: "Harmony, marriage, family, happiness, fulfillment.", emoji: "👨‍👩‍👧‍👦💧" },
    "Page of Cups": { description: "Creative ideas, emotional messages, intuition, curiosity.", emoji: "💌💧" },
    "Knight of Cups": { description: "Romance, charm, imagination, following the heart.", emoji: "🌹💧" },
    "Queen of Cups": { description: "Compassion, intuition, empathy, emotional security.", emoji: "👑💧" },
    "King of Cups": { description: "Emotional balance, compassion, diplomacy, control.", emoji: "🌊💧" },
    // Swords
    "Ace of Swords": { description: "Breakthrough, clarity, truth, mental power.", emoji: "💡⚔️" },
    "Two of Swords": { description: "Indecision, stalemate, blocked emotions, truce.", emoji: "🙈" },
    "Three of Swords": { description: "Heartbreak, sorrow, grief, emotional pain.", emoji: "💔⚔️" },
    "Four of Swords": { description: "Rest, recuperation, contemplation, meditation.", emoji: "🧘⚔️" },
    "Five of Swords": { description: "Conflict, defeat, ambition at a cost, winning at all costs.", emoji: "🤺" },
    "Six of Swords": { description: "Transition, moving on, rite of passage, leaving behind.", emoji: "🛶⚔️" },
    "Seven of Swords": { description: "Deception, strategy, betrayal, stealth.", emoji: "🤫⚔️" },
    "Eight of Swords": { description: "Restriction, self-imposed limitation, feeling trapped, powerlessness.", emoji: "🕸️⚔️" },
    "Nine of Swords": { description: "Anxiety, worry, fear, nightmares, despair.", emoji: "😰⚔️" },
    "Ten of Swords": { description: "Painful endings, rock bottom, betrayal, loss.", emoji: "😵⚔️" },
    "Page of Swords": { description: "Curiosity, new ideas, truth-seeking, vigilance.", emoji: "🧐⚔️" },
    "Knight of Swords": { description: "Ambition, action, haste, directness, drive.", emoji: "💨⚔️" },
    "Queen of Swords": { description: "Independence, sharp intellect, clarity, honesty.", emoji: "👑⚔️" },
    "King of Swords": { description: "Authority, truth, intellect, justice, mental clarity.", emoji: "⚖️👑⚔️" },
    // Pentacles
    "Ace of Pentacles": { description: "New financial opportunity, manifestation, prosperity, security.", emoji: "🌱🪙" },
    "Two of Pentacles": { description: "Balancing resources, adaptability, prioritization, juggling.", emoji: "🤹" },
    "Three of Pentacles": { description: "Teamwork, collaboration, skill, craftsmanship.", emoji: "🛠️🪙" },
    "Four of Pentacles": { description: "Conservation, security, possessiveness, control.", emoji: "🔒🪙" },
    "Five of Pentacles": { description: "Hardship, poverty, isolation, insecurity.", emoji: "🥶🪙" },
    "Six of Pentacles": { description: "Generosity, charity, sharing wealth, giving and receiving.", emoji: "🎁🪙" },
    "Seven of Pentacles": { description: "Patience, long-term view, investment, perseverance.", emoji: "🤔🪙" },
    "Eight of Pentacles": { description: "Diligence, craftsmanship, skill development, mastery.", emoji: "🔨🪙" },
    "Nine of Pentacles": { description: "Abundance, self-sufficiency, luxury, independence.", emoji: "🦋🪙" },
    "Ten of Pentacles": { description: "Legacy, inheritance, family wealth, long-term security.", emoji: "🏡🪙" },
    "Page of Pentacles": { description: "New opportunities, manifestation, financial news, diligence.", emoji: "📜🪙" },
    "Knight of Pentacles": { description: "Hard work, routine, responsibility, practicality.", emoji: "🐢🪙" },
    "Queen of Pentacles": { description: "Nurturing, practicality, security, resourcefulness.", emoji: "🌳🪙" },
    "King of Pentacles": { description: "Prosperity, security, leadership, abundance, enterprise.", emoji: "👑🪙" },
};

export const TAROT_DECK_WITH_INFO = TAROT_DECK_NAMES.map(name => {
    const info = CARD_INFO[name] || { description: "A card of deep meaning and cosmic influence.", emoji: "❓" };
    return { name, ...info };
});

export const getCardEmoji = (cardName) => {
    const cardInfo = TAROT_DECK_WITH_INFO.find(c => c.name === cardName);
    return cardInfo ? cardInfo.emoji : "❓";
};

export const drawCardsWithOrientation = (deckNames, count) => {
    const shuffled = [...deckNames].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, count).map(cardName => ({
        name: cardName,
        orientation: Math.random() < 0.5 ? 'upright' : 'reversed',
        emoji: getCardEmoji(cardName),
        deeperInterpretation: null
    }));
};

// Format interpretation text to highlight card names
export const formatInterpretation = (text, cards) => {
    if (!text || !cards || cards.length === 0) return text;
    let formattedText = text;
    const sortedCards = [...cards].sort((a, b) => b.name.length - a.name.length);

    sortedCards.forEach(card => {
        let namePartToMatch = card.name;
        let patternPrefix = "";

        if (namePartToMatch.startsWith("The ")) {
            patternPrefix = "(The\\s)?";
            namePartToMatch = namePartToMatch.substring(4);
        }

        const escapedNamePart = namePartToMatch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const cardNameRegexString = patternPrefix + escapedNamePart;
        const fullRegexString = `(${cardNameRegexString}(\\s*\\((upright|reversed)\\))?)`;
        const regex = new RegExp(fullRegexString, 'gi');

        formattedText = formattedText.replace(regex, (match) => {
            return `<strong class="text-amber-300 font-semibold">${match}</strong>`;
        });
    });
    return formattedText;
};
